/**
 * userColumnVisibilityApi — per-user visible-columns-per-table persisted via backend.
 *
 * Single GET on app load returns visible columns for all tables, keyed by tableKey.
 * PUT per table_key writes one table's column list (avoids cross-table races).
 */
import { apiSlice } from './apiSlice'

export type VisibleColumns = string[]
export type ColumnVisibilityDoc = Record<string, VisibleColumns>

export interface ColumnVisibilityResponse {
  columns: ColumnVisibilityDoc
}

export interface ColumnVisibilityPutPayload {
  tableKey: string
  columns: VisibleColumns
}

const userColumnVisibilityApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUserColumnVisibility: builder.query<ColumnVisibilityDoc, void>({
      query: () => ({ url: '/user-preferences/column-visibility' }),
      transformResponse: (res: ColumnVisibilityResponse) => res?.columns ?? {},
      providesTags: ['UserColumnVisibility'],
    }),

    putUserColumnVisibility: builder.mutation<{ message: string }, ColumnVisibilityPutPayload>({
      query: ({ tableKey, columns }) => ({
        url: `/user-preferences/column-visibility/${encodeURIComponent(tableKey)}`,
        method: 'PUT',
        body: { columns },
      }),
      onQueryStarted: async ({ tableKey, columns }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          userColumnVisibilityApi.util.updateQueryData('getUserColumnVisibility', undefined, (draft) => {
            draft[tableKey] = [...columns]
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),

    deleteUserColumnVisibility: builder.mutation<{ message: string }, string>({
      query: (tableKey) => ({
        url: `/user-preferences/column-visibility/${encodeURIComponent(tableKey)}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (tableKey, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          userColumnVisibilityApi.util.updateQueryData('getUserColumnVisibility', undefined, (draft) => {
            delete draft[tableKey]
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),
  }),
})

export const {
  useGetUserColumnVisibilityQuery,
  usePutUserColumnVisibilityMutation,
  useDeleteUserColumnVisibilityMutation,
} = userColumnVisibilityApi

export default userColumnVisibilityApi
